import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Check, ShieldCheck, AlertTriangle, FileSignature } from 'lucide-react'
import { useI18n } from '../../i18n'
import { signAgreementAPI } from '../../services/api'
import { canvasHasInk, signatureDataUrl } from '../../utils/signatureInk'
import PracticeAgreementModal from '../../components/PracticeAgreementModal' // s101 (D496)
import { practiceIsPdf } from '../../utils/practiceAgreement'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * SignAgreementScreen v225.2.0 — Phase B
 *
 * PUBLIC, no-login page reached via an SMS/email sign-link
 * (/sign-agreement/:token). Loads the bound agreement version, captures a
 * signature, and writes the case's patient_agreement (method: web_link).
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LBLUE = '#A7D9FC'
const ICE = '#E9F8FF'
const GREEN = '#66D170'

export default function SignAgreementScreen() {
  const { t } = useI18n()
  const { token } = useParams()

  const [status, setStatus] = useState('loading')  // loading | ready | invalid | done
  const [errorMsg, setErrorMsg] = useState('')
  const [data, setData] = useState(null)

  const [agreed, setAgreed] = useState(false)
  // s101 (D496): the physician's OWN agreement, when the office asks for it (data.practice).
  const [agreedPractice, setAgreedPractice] = useState(false)
  const [showPractice, setShowPractice] = useState(false)
  const [hasInk, setHasInk] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [signedAt, setSignedAt] = useState(null)

  const canvasRef = useRef(null)
  const drawingRef = useRef(false)

  // --- load token ---
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await signAgreementAPI.get(token)
        if (cancelled) return
        setData(res)
        setStatus('ready')
      } catch (err) {
        if (cancelled) return
        setErrorMsg(err?.error || 'This link is not valid.')
        setStatus('invalid')
      }
    })()
    return () => { cancelled = true }
  }, [token])

  // --- signature pad ---
  const sizeCanvas = useCallback(() => {
    const c = canvasRef.current
    if (!c) return
    const ratio = window.devicePixelRatio || 1
    const w = c.offsetWidth
    const h = c.offsetHeight
    c.width = w * ratio
    c.height = h * ratio
    const ctx = c.getContext('2d')
    ctx.scale(ratio, ratio)
    ctx.strokeStyle = NAVY
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  useEffect(() => {
    if (status !== 'ready') return
    const t = setTimeout(sizeCanvas, 60)
    window.addEventListener('resize', sizeCanvas)
    return () => { clearTimeout(t); window.removeEventListener('resize', sizeCanvas) }
  }, [status, sizeCanvas])

  const pointFromEvent = (e) => {
    const c = canvasRef.current
    const r = c.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    return { x: clientX - r.left, y: clientY - r.top }
  }
  const startDraw = (e) => {
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const p = pointFromEvent(e)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    drawingRef.current = true
  }
  const moveDraw = (e) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const p = pointFromEvent(e)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    // v242.64.0 (BUG-5): this screen's hasInk was already the CORRECT definition
    // and is the one the other two were unified onto. It now calls the shared
    // predicate rather than keeping a fourth private copy of the idea
    // (ORELLA_RULES.md 1h — calling the same function is a property, a comment
    // saying two places agree is only a promise).
    if (!hasInk && canvasHasInk(canvasRef.current)) setHasInk(true)
  }
  const endDraw = () => { drawingRef.current = false }
  const clearSig = () => {
    const c = canvasRef.current
    if (!c) return
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    setHasInk(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setErrorMsg('')
    try {
      // v242.64.0 (BUG-5): last raw toDataURL on a consent path. handleSubmit is
      // already gated on hasInk, so this is defence in depth, not a fix — but it
      // means NO signature in this app is produced by a call that always succeeds.
      const signature = signatureDataUrl(canvasRef.current)
      if (!signature) { setSubmitting(false); setErrorMsg('Please sign in the box above.'); return }
      const res = await signAgreementAPI.submit(token, {
        signature, agreed: true,
        ...(data?.practice ? { agreedPractice: true, practiceTemplateId: data.practice.templateId } : {}),
      })
      setSignedAt(res?.signedAt || new Date().toISOString())
      setStatus('done')
    } catch (err) {
      setErrorMsg(err?.error || 'Something went wrong. Please try again.')
      setSubmitting(false)
      // s101 (D496): the doctor published a newer agreement while this page was open - show it.
      if (err?.reason === 'practice_updated') {
        try { const fresh = await signAgreementAPI.get(token); setData(fresh); setAgreedPractice(false) } catch (e) { /* the message stands */ }
      }
    }
  }

  // ── shells ──
  const Shell = ({ children }) => (
    <div className="screen-min-h" style={{ background: '#eef2f6', display: 'flex', justifyContent: 'center' }}>
      <div className="screen-min-h" style={{ width: '100%', maxWidth: 480, background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: NAVY, color: '#fff', padding: '20px 20px 18px' }}>
          <div style={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: 16 }}>orella</div>
          {data?.provider && (
            <div style={{ fontSize: 12, color: LBLUE, marginTop: 2 }}>
              {data.provider}{data.practice ? ` · ${data.practice}` : ''}
            </div>
          )}
        </div>
        <div style={{ padding: 20, flex: 1, fontFamily: "'Inter', sans-serif", color: NAVY }}>
          {children}
        </div>
      </div>
    </div>
  )

  if (status === 'loading') {
    return (
      <Shell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0', color: '#94a3b8' }}>
          <OrellaSpinner size={20} tone="inherit" style={{ marginRight: 8 }} /> {t('signAgreement.loadingAgreement', 'Loading agreement…')}
        </div>
      </Shell>
    )
  }

  if (status === 'invalid') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '60px 12px' }}>
          <div style={{ width: 72, height: 72, borderRadius: 22, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <AlertTriangle size={34} color="#B8860B" />
          </div>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{t('signAgreement.linkUnavailable', 'Link unavailable')}</div>
          <p style={{ color: '#64748b', fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>{errorMsg}</p>
        </div>
      </Shell>
    )
  }

  if (status === 'done') {
    return (
      <Shell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '50px 16px' }}>
          <div style={{ width: 78, height: 78, borderRadius: 24, background: '#d5efd3', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
            <Check size={38} color="#2f9e44" />
          </div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>You're all set</div>
          <p style={{ color: '#64748b', fontSize: 13.5, marginTop: 8, maxWidth: 260, lineHeight: 1.5 }}>
            {t('signAgreement.sentBody', { title: data?.title || t('share.patientCareAgreement', 'Patient Care Agreement'), practice: data?.practice || data?.provider || t('signAgreement.theOffice', 'the office'), defaultValue: 'Your signed {{title}} has been sent to {{practice}}. You can close this page.' })}
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: ICE, color: BLUE, fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 999, marginTop: 18 }}>
            <ShieldCheck size={14} /> {t('signAgreement.signed', 'Signed')}{data?.version ? ` · v${data.version}` : ''} · {new Date(signedAt).toLocaleDateString()}
          </div>
        </div>
      </Shell>
    )
  }

  // status === 'ready'
  const sections = Array.isArray(data?.sections) ? data.sections : []
  // s101 (D496): Orella's block shows only while it is unsigned on this case; the physician's own
  //   agreement shows when the office asks for it. Each has its own tick; one signature covers both.
  const showOrella = data?.needsPlatform !== false
  const practice = data?.practice || null
  const canSubmit = (!showOrella || agreed) && (!practice || agreedPractice) && (showOrella || !!practice) && hasInk && !submitting

  return (
    <Shell>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: ICE, color: BLUE, fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 999 }}>
        <FileSignature size={13} /> {t('signAgreement.visitAgreement', 'Visit agreement')}
      </span>
      <div style={{ fontSize: 18, fontWeight: 700, margin: '14px 0 6px' }}>{t('signAgreement.pleaseReviewAndSign')}</div>
      <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
        Hi {data?.patientFirstName || 'there'} — before your visit{data?.provider ? ` with ${data.provider}` : ''}, please review and
        sign the {data?.title || 'Patient Care Agreement'}. This takes about a minute and no app or login is needed.
      </p>

      {showOrella && (<>
      {/* Agreement */}
      <div style={{ marginTop: 14, border: '1px solid #e8eef4', borderRadius: 14, background: '#f9fbfd', maxHeight: 260, overflow: 'auto', padding: 14, fontSize: 12, lineHeight: 1.55, color: '#37475a' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>{data?.title || 'Patient Care Agreement'}</div>
        {sections.length === 0 && (
          <p style={{ marginTop: 8, color: '#9aa7b4' }}>{t('signAgreement.couldNotLoad', 'The agreement could not be loaded. Please contact the office.')}</p>
        )}
        {sections.map((s, i) => (
          <div key={i}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: BLUE, margin: '10px 0 4px', fontWeight: 600 }}>
              {i + 1} · {s.heading}
            </div>
            <p>{s.body}</p>
          </div>
        ))}
      </div>

      {/* Agree */}
      <div
        onClick={() => setAgreed(v => !v)}
        style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, padding: 12, border: `1.5px solid ${agreed ? GREEN : '#e2e8f0'}`, borderRadius: 12, cursor: 'pointer', background: agreed ? '#f0fdf4' : '#fff' }}
      >
        <div style={{ width: 22, height: 22, borderRadius: 7, border: `2px solid ${agreed ? GREEN : '#cbd5e1'}`, background: agreed ? GREEN : 'transparent', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {agreed && <Check size={14} color="#fff" />}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>
          {t('signAgreement.readAndAgree', { title: data?.title || t('share.patientCareAgreement') })}
        </div>
      </div>
      </>)}

      {practice && (
        <div data-testid="sign-practice-agreement" style={{ marginTop: 14 }}>
          <div style={{ border: '1px solid #e8eef4', borderRadius: 14, background: '#f9fbfd', padding: 14, fontSize: 12, lineHeight: 1.55, color: '#37475a', maxHeight: practiceIsPdf(practice) ? 'none' : 260, overflow: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>{practice.title}</div>
            {practiceIsPdf(practice) ? (
              <button type="button" onClick={() => setShowPractice(true)} style={{ marginTop: 8, color: BLUE, fontWeight: 700, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
                {t('agreement.readIt', 'Read it')}
              </button>
            ) : (practice.sections || []).map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: BLUE, margin: '10px 0 4px', fontWeight: 600 }}>{i + 1} · {s.heading}</div>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <div onClick={() => setAgreedPractice((v) => !v)}
            style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10, padding: 12, border: `1.5px solid ${agreedPractice ? GREEN : '#e2e8f0'}`, borderRadius: 12, cursor: 'pointer', background: agreedPractice ? '#f0fdf4' : '#fff' }}>
            <div style={{ width: 22, height: 22, borderRadius: 7, border: `2px solid ${agreedPractice ? GREEN : '#cbd5e1'}`, background: agreedPractice ? GREEN : 'transparent', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {agreedPractice && <Check size={14} color="#fff" />}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>{t('agreement.iAgreeTo', { title: practice.title, defaultValue: 'I have read and agree to the {{title}}' })}</div>
          </div>
          <PracticeAgreementModal isOpen={showPractice} onClose={() => setShowPractice(false)} agreement={practice} t={t} />
        </div>
      )}

      {/* Signature */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, fontWeight: 600, color: NAVY, margin: '18px 0 6px' }}>
        <span>{t('signAgreement.signBelow', 'Sign below')}</span>
        <span onClick={clearSig} style={{ color: BLUE, cursor: 'pointer' }}>{t('signAgreement.clear', 'Clear')}</span>
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={startDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={moveDraw}
        onTouchEnd={endDraw}
        style={{ width: '100%', height: 150, border: '1.5px dashed #c4d4e3', borderRadius: 14, background: '#fff', touchAction: 'none', display: 'block' }}
      />

      {errorMsg && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 12px', borderRadius: 10, background: '#FEE2E2', color: '#991B1B', fontSize: 12.5, fontWeight: 600 }}>
          <AlertTriangle size={15} /> {errorMsg}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{ margin: '16px 0 4px', width: '100%', border: 'none', borderRadius: 999, padding: 14, fontSize: 14, fontWeight: 700, color: '#fff', background: canSubmit ? NAVY : '#cbd5e1', cursor: canSubmit ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        {submitting ? <OrellaSpinner size={16} tone="inherit" /> : <Check size={16} />}
        {submitting ? 'Submitting…' : 'Submit signature'}
      </button>
      <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}>
        {t('signAgreement.eSignConsent')}
        {data?.version ? t('signAgreement.boundToVersion', { version: data.version }) : ''}
        {data?.effectiveDate ? t('signAgreement.effectiveOn', { date: data.effectiveDate }) : ''}.
      </p>
    </Shell>
  )
}
